import _ from 'lodash'
import { Codec, Messages } from '@probe/shared'
import { Communicator } from './communicator'
import { WebSocketManager } from './web-socket-manager'
import { createConnectTransport } from '@connectrpc/connect-web'
import { createClient, type Client } from '@connectrpc/connect'
import type { DescService } from '@bufbuild/protobuf'

export class CommunicatorImpl implements Communicator.Service {
    private ongoing = new Map<
        string,
        [(v: Messages.Response) => void, (e: Messages.Failure) => void]
    >()
    private handlers = new Set<Communicator.NotificationHandler>()
    constructor(
        private readonly websocketManager: WebSocketManager,
        private readonly codec: Codec
    ) {
        this.websocketManager.addListener({
            onMessage: (data: string) => {
                const receivedMessage = codec.decode(data)

                if (receivedMessage instanceof Messages.Envelope) {
                    const ongoing = this.ongoing.get(receivedMessage.requestId)

                    if (ongoing) {
                        this.ongoing.delete(receivedMessage.requestId)

                        if (receivedMessage.message instanceof Messages.Failure) {
                            ongoing[1](receivedMessage.message)
                        } else {
                            ongoing[0](receivedMessage.message)
                        }
                    }
                } else if (receivedMessage instanceof Messages.Notification) {
                    Array.from(this.handlers.values()).forEach((handler) =>
                        handler.handle(receivedMessage)
                    )
                } else {
                    throw new Error(
                        `Unexpected message type from server. Expected Messages.Envelope. Was: ${receivedMessage.constructor.name}`
                    )
                }
            }
        })
    }

    addHandler(handler: Communicator.NotificationHandler): void {
        this.handlers.add(handler)
    }
    removeHandler(handler: Communicator.NotificationHandler): void {
        this.handlers.delete(handler)
    }

    private generateId(): string {
        return _.uniqueId()
    }

    doNotification(notification: Messages.Notification): Promise<void> {
        this.websocketManager.send(this.codec.encode(notification))

        return Promise.resolve()
    }
    doRequest(request: Messages.Request): Promise<Messages.Response> {
        const requestId = this.generateId()
        const envelope = new Messages.Envelope(requestId, request)
        const promise = new Promise<Messages.Response>((resolve, reject) => {
            this.ongoing.set(requestId, [resolve, reject])
            this.websocketManager.send(this.codec.encode(envelope))
        })

        return promise
    }

    start(): void {
        this.websocketManager.start()
    }
    stop(): void {
        this.websocketManager.stop()
    }

    private transport = createConnectTransport({
        baseUrl: `/rpc`,
        interceptors: []
    })

    rpcClient<S extends DescService>(service: S): Client<S> {
        return createClient(service, this.transport)
    }
}
